import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  FullTextDetail,
  FulltextService,
  Order,
  OrdersService,
} from '@compumark/order-management-client';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UpdateOrderModel } from '../../models/update-order-model';

@Component({
  selector: 'app-full-text-lookup',
  templateUrl: './full-text-lookup.component.html',
  styleUrls: ['./full-text-lookup.component.scss'],
  standalone: false,
})
export class FullTextLookupComponent implements OnInit, OnDestroy {
  data = inject<{
    model: UpdateOrderModel;
  }>(MAT_DIALOG_DATA);
  orderService = inject(OrdersService);
  fulltextService = inject(FulltextService);
  private fb = inject(FormBuilder);

  public order: Order;
  public fullTextLookupForm!: FormGroup;
  public registers!: Array<string>;
  public selectedFullTextDetails: Set<FullTextDetail> = new Set<FullTextDetail>(
    [],
  );
  public fullTextDetails!: FullTextDetail[];
  private destroy$ = new Subject<void>();

  constructor() {
    this.order = this.data.model.order!;
    this.fullTextLookupForm = this.fb.group({
      register: this.fb.control('', []),
      trademarkNumber: this.fb.control('', []),
    });
  }

  ngOnInit(): void {
    this.orderService
      .getRegisters()
      .pipe(takeUntil(this.destroy$))
      .subscribe((registers) => {
        this.registers = registers!;
      });
    if (this.order.fullTextDetails) {
      this.fulltextService
        .getLinkedFullText(this.order.fullTextDetails)
        .subscribe((linkedFullTextData: FullTextDetail[]) => {
          this.selectedFullTextDetails = new Set<FullTextDetail>(
            linkedFullTextData.map((obj: FullTextDetail) => ({
              ...obj,
              selected: true,
            })),
          );
          this.data.model.originalOrder!.fullTextDetails =
            cloneDeep(linkedFullTextData);
          this.data.model.order!.fullTextDetails =
            cloneDeep(linkedFullTextData);
        });
    }
  }

  findFullTextInfoClick(): void {
    this.fulltextService
      .fullTextSearch(
        this.fullTextLookupForm.value.trademarkNumber,
        this.fullTextLookupForm.value.register,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        data = data.map((obj) => ({ ...obj, selected: false }));
        this.updateSelection(data);
        this.fullTextDetails = data;
      });
  }

  // changing fulltext selected property if that's already selected by user
  updateSelection(fulltextArray: FullTextDetail[]): void {
    for (const [i, item] of fulltextArray.entries()) {
      const selectedObj = this.objectExists(
        'trademarkGuid',
        item.trademarkGuid!,
        this.selectedFullTextDetails,
      );
      if (selectedObj) {
        fulltextArray[i] = selectedObj;
      }
    }
  }

  objectExists(
    property: keyof FullTextDetail,
    value: string,
    listOfData: any,
  ): FullTextDetail | undefined {
    for (const item of listOfData) {
      if (item[property] === value) {
        return item;
      }
    }
    return undefined;
  }

  toggleSelection(data: any): void {
    if (data.selected) {
      this.selectedFullTextDetails.delete(data);
    } else {
      this.selectedFullTextDetails.add(data);
    }
    data.selected = !data.selected;
    this.changeOrderModel(data);
  }

  isButtonDisabled(): boolean {
    return !(
      this.fullTextLookupForm.get('trademarkNumber')?.value?.trim() &&
      this.fullTextLookupForm.get('register')?.value
    );
  }

  private changeOrderModel = (data: FullTextDetail) => {
    const index =
      this.order.fullTextDetails?.findIndex(
        (obj) => obj.trademarkGuid === data.trademarkGuid,
      ) ?? -1;

    if (index !== -1) {
      this.order.fullTextDetails?.splice(index, 1);
    } else {
      this.order.fullTextDetails?.push(data);
    }

    if (
      this.order.fullTextDetails?.length ===
      this.data.model.originalOrder?.fullTextDetails?.length
    ) {
      const fulltextEqual =
        this.order.fullTextDetails?.every((obj) =>
          this.data.model.originalOrder?.fullTextDetails?.some(
            (originalObj) =>
              originalObj.trademarkNumber === obj.trademarkNumber &&
              originalObj.register === obj.register,
          ),
        ) ?? false;

      if (fulltextEqual) {
        this.order.fullTextDetails = cloneDeep(
          this.data.model.originalOrder?.fullTextDetails,
        );
      }
    }
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
